/* ─────────────────────────────────────────────
   COURSE CONFIGURATION - the only file to edit between groups

   Everything here is consumed twice: by the React pages at runtime and by
   src/seo/head.mjs when it builds the Course / CourseInstance JSON-LD (which
   scripts/prerender.mjs also renders at build time). Change a value once and
   the page copy, the schema and the prerendered HTML all follow.

   .mjs on purpose: the prerender script is plain Node with no bundler, so it
   must be able to import this directly. Keep it free of JSX and React.
   ───────────────────────────────────────────── */

/* ── Basic course - "Od nule do prve mušterije" ──
   BEFORE EVERY NEW GROUP, update: startDate, startDateLabel, priceEur,
   priceRsdApprox, maxAttendees, priceValidFrom and the early-bird block.
   The page copy pulls these in through i18next interpolation, so there are no
   dates or prices hard-coded in the translations. */
export const BASIC_COURSE = {
  /** ISO date, feeds CourseInstance.startDate. */
  startDate: '2026-09-28',
  /** Human-readable start date shown on the page, per language. */
  startDateLabel: {
    sr: '28. septembar 2026.',
    en: '28 September 2026',
    de: '28. September 2026',
  },
  /** Same date inside a sentence ("group starts on …"). Serbian needs the
      genitive here, so it cannot be derived from startDateLabel. */
  startDateInline: {
    sr: '28. septembra',
    en: '28 September',
    de: '28. September',
  },
  /** Group size cap - CourseInstance.maximumAttendeeCapacity. */
  maxAttendees: 6,
  /** Price in EUR (Offer.price) and the rounded RSD equivalent shown as a hint. */
  priceEur: 345,
  priceRsdApprox: 40000,
  /** Deposit due on booking; the rest is paid on day one. */
  depositEur: 100,
  /** ISO 8601 duration - 4 teaching days (the 5th day is a week later). */
  workload: 'P4D',
  /** Offer.validFrom. Bump it whenever the price changes. */
  priceValidFrom: '2026-08-25',
  /** Early-bird deadline. The seat count ("prve tri") is spelled out in the
      copy itself - see educations.basic.practical.earlyBird in the locales. */
  earlyBird: {
    deadlineLabel: { sr: '5. septembra', en: '5 September', de: '5. September' },
  },
  /** Reference price range for one professional makeup session in Niš (RSD),
      used by the "the course pays for itself" block. The break-even count
      ("dvanaest mušterija") is likewise spelled out in the copy. */
  marketPriceRsd: { from: 3400, to: 5000 },
};

/* ── Advanced course - "Sledeći korak" ──
   The price is public: the practical-info table, the card on /ponude and the
   CourseInstance offer all read it from here.

   To take it private again: flip showPrice to false. The page then falls back
   to the "on request" wording and the JSON-LD omits the whole "offers" block -
   an empty or zero price is invalid and the Rich Results Test flags it. */
export const ADVANCED_COURSE = {
  showPrice: true,
  priceEur: 290,
  priceCurrency: 'EUR',
  /** Deposit due on booking; the rest is paid on day one, as on the basic course. */
  depositEur: 100,
  /** ISO 8601 duration - two one-on-one days. */
  workload: 'P2D',
};

/* ── Student testimonials ──────────────────────
   EMPTY ON PURPOSE. While this array has no entries the whole "Utisci
   polaznica" section is skipped - no heading, no placeholder, no empty boxes.

   To add one, push an object with all four keys:

     {
       ime:   'Marija P.',                       // name as it should appear
       tekst: 'Posle kursa sam...',              // the quote itself
       slika: 'https://res.cloudinary.com/...',  // photo URL, or '' for none
       kurs:  'basic',                           // 'basic' | 'advanced'
     }

   `kurs` decides which page shows the testimonial: 'basic' → /educations,
   'advanced' → the advanced course page. Photos go through Cloudinary like
   the rest of the site's images (see src/utils/cld.js). */
export const TESTIMONIALS = [
  // ← add testimonials here
];

/** Testimonials for one course, in page order. */
export function testimonialsFor(kurs) {
  return TESTIMONIALS.filter((item) => item.kurs === kurs);
}
